import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PW_RULES = [
  { key: "length", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "One number", test: (p) => /[0-9]/.test(p) },
  { key: "symbol", label: "One symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

/**
 * ResetPassword — token-based public page.
 *
 * Flow:
 *   • On mount: call /validate/{token}. If invalid, render a dead-end card.
 *   • On submit: POST /confirm. On 200, show success + CTA to /login.
 *   • Password policy indicators match the backend validator.
 */
export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [validation, setValidation] = useState(null); // {valid, reason?, expires_at?}
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Iter 166 — defensive sanitation. Email clients (Outlook, Gmail mobile)
        // sometimes append trailing punctuation, whitespace, or wrap the URL with
        // tracking suffixes. Strip anything that's not a valid urlsafe-b64 char.
        const cleaned = String(token || "").trim().replace(/[^A-Za-z0-9_-]/g, "");
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[reset-password] token diag", {
            raw_len: (token || "").length,
            cleaned_len: cleaned.length,
            match: cleaned === (token || ""),
          });
        }
        const r = await axios.get(`${API}/auth/password-reset/validate/${cleaned}`);
        if (mounted) setValidation(r.data);
      } catch {
        if (mounted) setValidation({ valid: false, reason: "error" });
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const ruleResults = PW_RULES.map((r) => ({ ...r, passed: r.test(pw) }));
  const allOk = ruleResults.every((r) => r.passed);
  const matches = pw && pw === pw2;

  const onSubmit = async (e) => {
    e?.preventDefault();
    setErr(null);
    if (!allOk) { setErr("Password does not meet the policy."); return; }
    if (!matches) { setErr("Passwords do not match."); return; }
    setLoading(true);
    try {
      const cleanedToken = String(token || "").trim().replace(/[^A-Za-z0-9_-]/g, "");
      await axios.post(`${API}/auth/password-reset/confirm`, { token: cleanedToken, new_password: pw });
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      if (detail.code === "weak_password") {
        setErr(`Password does not meet the policy: ${(detail.reasons || []).join(", ")}`);
      } else if (detail.code === "invalid_or_expired_token") {
        setErr("This reset link is no longer valid. Request a new one.");
        setValidation({ valid: false, reason: "expired_or_used" });
      } else if (detail.code === "rate_limited") {
        setErr(`Too many attempts. Try again in ${detail.retry_after || 60}s.`);
      } else {
        setErr("Could not reset your password. Please try again.");
      }
    } finally { setLoading(false); }
  };

  if (validation === null) {
    return <CenterShell><div data-testid="reset-password-loading" style={{ padding: 32, textAlign: "center" }}><Loader2 className="animate-spin" style={{ width: 18, height: 18 }} /></div></CenterShell>;
  }

  if (!validation.valid) {
    return (
      <CenterShell>
        <div className="card" style={{ padding: 24 }} data-testid="reset-password-invalid">
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <AlertTriangle style={{ width: 18, height: 18, color: "#A32D2D", marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                This reset link is no longer valid
              </div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55 }}>
                {validation.reason === "expired" && "The link has expired. Reset links are valid for 60 minutes."}
                {validation.reason === "already_used" && "This link has already been used."}
                {(validation.reason === "not_found" || validation.reason === "missing_token" || validation.reason === "error" || validation.reason === "expired_or_used") && "The link may have expired or already been used."}
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/forgot-password")} className="btn btn-primary" data-testid="reset-password-request-new"
            style={{ marginTop: 18, width: "100%", justifyContent: "center", padding: 10, fontSize: 12 }}>
            Request a new link
          </button>
        </div>
      </CenterShell>
    );
  }

  if (done) {
    return (
      <CenterShell>
        <div className="card" style={{ padding: 24 }} data-testid="reset-password-success">
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <CheckCircle2 style={{ width: 18, height: 18, color: "#16a34a", marginTop: 1 }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Password updated</div>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55 }}>
                Your password has been reset. You can now sign in with the new one.
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/login")} className="btn btn-primary" data-testid="reset-password-back-to-login"
            style={{ marginTop: 18, width: "100%", justifyContent: "center", padding: 10, fontSize: 12 }}>
            Sign in
          </button>
        </div>
      </CenterShell>
    );
  }

  return (
    <CenterShell>
      <form onSubmit={onSubmit} className="card" style={{ padding: 24 }} data-testid="reset-password-form">
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Set a new password</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginBottom: 16 }}>
          Pick something unique — your new password must meet every rule below.
        </div>

        <PwInput value={pw} onChange={setPw} show={show} setShow={setShow} testid="reset-password-input" autoFocus placeholder="New password" />
        <div style={{ marginTop: 10 }}>
          <PwInput value={pw2} onChange={setPw2} show={show} setShow={setShow} testid="reset-password-confirm-input" placeholder="Confirm new password" />
        </div>

        <ul data-testid="reset-password-rules" style={{ listStyle: "none", padding: 0, margin: "14px 0 0", fontSize: 11 }}>
          {ruleResults.map((r) => (
            <li key={r.key} data-testid={`reset-password-rule-${r.key}`} data-passed={r.passed}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0", color: r.passed ? "#16a34a" : "var(--text-tertiary)" }}>
              {r.passed ? <Check style={{ width: 12, height: 12 }} /> : <X style={{ width: 12, height: 12 }} />}
              {r.label}
            </li>
          ))}
          <li data-testid="reset-password-rule-match" data-passed={matches ? "true" : "false"}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0", color: matches ? "#16a34a" : "var(--text-tertiary)" }}>
            {matches ? <Check style={{ width: 12, height: 12 }} /> : <X style={{ width: 12, height: 12 }} />}
            Passwords match
          </li>
        </ul>

        {err && <div data-testid="reset-password-error" style={{ marginTop: 12, padding: 10, background: "#FFEBEB", color: "#A32D2D", borderRadius: 6, fontSize: 11 }}>{err}</div>}

        <button type="submit" disabled={loading || !allOk || !matches} className="btn btn-primary" data-testid="reset-password-submit-btn"
          style={{ marginTop: 16, width: "100%", justifyContent: "center", padding: 10, fontSize: 12 }}>
          {loading ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : "Update password"}
        </button>

        <div style={{ marginTop: 14, textAlign: "center" }}>
          <Link to="/login" style={{ fontSize: 11, color: "var(--blue)" }}>← Back to sign in</Link>
        </div>
      </form>
    </CenterShell>
  );
}

function CenterShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--page-bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} data-testid="reset-password-page">
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ marginBottom: 24, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#0F2942" }}>Reset your password</div>
        </div>
        {children}
      </div>
    </div>
  );
}

function PwInput({ value, onChange, show, setShow, testid, placeholder, autoFocus }) {
  return (
    <div style={{ position: "relative" }}>
      <Lock style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#888" }} />
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} autoFocus={autoFocus}
        data-testid={testid} placeholder={placeholder} className="input"
        style={{ paddingLeft: 30, paddingRight: 36, fontSize: 12, width: "100%" }} />
      <button type="button" onClick={() => setShow(!show)} aria-label="Toggle password visibility"
        style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#888" }}>
        {show ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
      </button>
    </div>
  );
}
